import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  LifeBuoy,
  Loader2,
  RefreshCw,
  Mail,
  Monitor,
  Inbox,
  CircleDot,
  Clock4,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { id: "new", label: "New", icon: CircleDot, dot: "bg-amber-500" },
  { id: "in_progress", label: "In progress", icon: Clock4, dot: "bg-blue-500" },
  { id: "resolved", label: "Resolved", icon: CheckCircle2, dot: "bg-emerald-500" },
];

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-700 px-2 py-0.5 rounded-md bg-zinc-100">
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export default function SupportInbox() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/reports");
      setReports(data || []);
      if (data?.length && !selected) setSelected(data[0]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.is_support_admin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_support_admin]);

  const filtered = useMemo(() => {
    if (filter === "all") return reports;
    return reports.filter((r) => (r.status || "new") === filter);
  }, [reports, filter]);

  // Re-select the same id from the new filtered list if possible
  useEffect(() => {
    if (!selected) return;
    const stillVisible = filtered.find((r) => r.id === selected.id);
    if (!stillVisible) setSelected(filtered[0] || null);
  }, [filtered, selected]);

  const counts = useMemo(() => {
    const c = { all: reports.length, new: 0, in_progress: 0, resolved: 0 };
    for (const r of reports) c[r.status || "new"] = (c[r.status || "new"] || 0) + 1;
    return c;
  }, [reports]);

  const setStatus = async (id, status) => {
    setUpdating(true);
    try {
      await api.patch(`/support/reports/${id}`, { status });
      setReports((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setUpdating(false);
    }
  };

  if (!user) return null;
  if (!user.is_support_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center max-w-sm" data-testid="support-not-admin">
          <LifeBuoy className="w-10 h-10 mx-auto text-zinc-400 mb-4" />
          <p className="font-heading text-xl text-zinc-900">Admins only</p>
          <p className="text-sm text-zinc-600 mt-2">
            This inbox is restricted to the email listed in <code>SUPPORT_ADMIN_EMAIL</code>.
          </p>
          <Button className="mt-6" onClick={() => nav("/app")} data-testid="support-back-app">
            Back to app
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="support-inbox-page">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/app")}
              data-testid="support-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="w-px h-6 bg-zinc-200" />
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-zinc-700" />
              <p className="font-heading text-lg tracking-tight">Support inbox</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="support-refresh-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="support-filter-chips">
          {[
            { id: "all", label: "All" },
            ...STATUSES,
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3.5 h-9 rounded-lg text-sm font-medium transition-colors duration-200 border flex items-center gap-2 ${
                filter === f.id
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`support-filter-${f.id}`}
            >
              {f.label}
              <span className={`text-xs ${filter === f.id ? "text-zinc-300" : "text-zinc-400"}`}>
                {counts[f.id] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="support-loading">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-24" data-testid="support-empty">
            <Inbox className="w-10 h-10 mx-auto text-zinc-300 mb-4" />
            <p className="font-heading text-xl text-zinc-900">No reports yet</p>
            <p className="text-sm text-zinc-500 mt-2">
              When a user submits a problem from inside the app, it'll show up here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
            {/* List */}
            <aside
              className="border border-zinc-200 rounded-xl bg-white overflow-hidden"
              data-testid="support-report-list"
            >
              <ul className="divide-y divide-zinc-100 max-h-[calc(100vh-220px)] overflow-y-auto">
                {filtered.map((r) => {
                  const active = selected?.id === r.id;
                  return (
                    <li key={r.id}>
                      <button
                        onClick={() => setSelected(r)}
                        className={`w-full text-left px-4 py-4 transition-colors ${
                          active ? "bg-zinc-100" : "hover:bg-zinc-50"
                        }`}
                        data-testid={`support-report-row-${r.id}`}
                      >
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <p className="font-medium text-zinc-900 line-clamp-1">{r.subject}</p>
                          <StatusBadge status={r.status} />
                        </div>
                        <p className="text-sm text-zinc-600 line-clamp-2">{r.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                          <span className="truncate max-w-[180px]">{r.user_email}</span>
                          <span>·</span>
                          <span>{new Date(r.created_at).toLocaleString()}</span>
                        </div>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li
                    className="px-4 py-10 text-center text-sm text-zinc-500"
                    data-testid="support-filter-empty"
                  >
                    No reports in this view.
                  </li>
                )}
              </ul>
            </aside>

            {/* Detail */}
            <section
              className="border border-zinc-200 rounded-xl bg-white p-6 md:p-8"
              data-testid="support-report-detail"
            >
              {selected ? (
                <>
                  <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-2">
                        Report
                      </p>
                      <h1 className="font-heading text-2xl md:text-3xl tracking-tight text-zinc-900 break-words">
                        {selected.subject}
                      </h1>
                      <p className="text-sm text-zinc-500 mt-2">
                        {new Date(selected.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-testid="support-status-actions">
                      {STATUSES.map((s) => {
                        const Icon = s.icon;
                        const active = (selected.status || "new") === s.id;
                        return (
                          <Button
                            key={s.id}
                            size="sm"
                            variant={active ? "default" : "outline"}
                            onClick={() => setStatus(selected.id, s.id)}
                            disabled={updating || active}
                            data-testid={`support-set-status-${s.id}`}
                          >
                            <Icon className="w-3.5 h-3.5 mr-1.5" />
                            {s.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-sm">
                    <div className="border border-zinc-100 rounded-lg p-3">
                      <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> From
                      </p>
                      <p className="text-zinc-900 truncate">{selected.user_name || "—"}</p>
                      <p className="text-zinc-500 text-xs truncate">{selected.user_email}</p>
                      {selected.discipline && (
                        <p className="text-xs text-zinc-500 mt-1">{selected.discipline}</p>
                      )}
                    </div>
                    <div className="border border-zinc-100 rounded-lg p-3">
                      <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                        <Monitor className="w-3 h-3" /> Page
                      </p>
                      <p className="text-zinc-900 font-mono text-xs break-words">
                        {selected.page_path || "—"}
                      </p>
                    </div>
                    <div className="border border-zinc-100 rounded-lg p-3">
                      <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-1.5">
                        User agent
                      </p>
                      <p className="text-zinc-600 text-xs font-mono break-words line-clamp-3">
                        {selected.user_agent || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                      What happened
                    </p>
                    <div
                      className="text-zinc-900 leading-relaxed whitespace-pre-wrap"
                      data-testid="support-report-description"
                    >
                      {selected.description}
                    </div>
                  </div>

                  {selected.screenshot_data_url && (
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">
                        Screenshot
                      </p>
                      <a
                        href={selected.screenshot_data_url}
                        target="_blank"
                        rel="noreferrer"
                        className="block border border-zinc-200 rounded-lg overflow-hidden hover:opacity-90 transition-opacity max-w-2xl"
                        data-testid="support-report-screenshot"
                      >
                        <img
                          src={selected.screenshot_data_url}
                          alt="User-attached screenshot"
                          className="w-full h-auto"
                        />
                      </a>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-zinc-500 text-center py-12">
                  Select a report from the list to view details.
                </p>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
